import { createAction } from '@reduxjs/toolkit'
import cloneDeep from 'lodash/cloneDeep'
import { all, call, put, select, takeEvery } from 'redux-saga/effects'

import { createModification, editModification } from '../../../api/projects-api'
import { IMaterialModificationConflict, IMaterialModificationConflictAPI } from '../../../models/masterSetPlan'
import { DRAWABLE_TYPES } from '../../../shared/constants/drawable-types'
import managers from '../../lib/managers'
import PaperManager from '../../lib/managers/PaperManager'
import { Select, Workspace } from '../../lib/toolBoxes/2D'
import { selectDrawableLocations } from '../../slices/2D'
import {
    discardMaterialModificationConflicts,
    selectMaterialModificationConflicts,
    selectSavedMaterialModifications,
    updateMaterialModificationConflictToSave,
    updateSavedMaterialModification,
} from '../../slices/masterSetPlan'
import { selectProjectId } from '../../slices/project'
import { IMUP2DDrawableLocation } from '../../types'

export const deleteModificationAction = createAction<number[]>('deleteModification')

// A modification whose geometry matches its base location is how conflict resolution records
// "deleted for this option" (see generateMaterialModsToAdjustAndHide / adjustMaterialModifications).
// Rewrite saved modifications into that state rather than deleting the record, which would
// instead restore the base material to the option.
export function* markSavedModificationsAsDeleted(savedMods: IMaterialModificationConflict[]) {
    const projectId: number | null = yield select(selectProjectId)
    const manager: PaperManager | null = yield call(managers.get2DManager)

    if (!projectId || !manager || !savedMods.length) return

    const workspaceTool: Workspace = yield call(manager.getTool, Workspace.NAME)
    const drawableLocations: IMUP2DDrawableLocation[] = yield select(selectDrawableLocations)
    const allSavedMods: IMaterialModificationConflict[] = yield select(selectSavedMaterialModifications)
    // Records are tracked by id, not location: distinct records for different options can share a
    // material_location_id, and each must be edited.
    const handledModIds = new Set<number>()
    // New deletion records are keyed by location and option set, since a split sibling gets both an
    // edit (the narrowed, still-live record) and a create for the same location in one pass.
    const handledCreateKeys = new Set<string>()
    const getCreateKey = (mod: IMaterialModificationConflict) =>
        `${mod.material_location_id}:${[...mod.option_ids].sort((a, b) => a - b).join('_')}`

    for (const savedMod of savedMods) {
        if (handledModIds.has(savedMod.id)) continue

        const baseLocation = drawableLocations.find((loc) => loc.opening_location_id === savedMod.material_location_id)

        if (!baseLocation) continue

        const deletedMod = toDeletedModification(savedMod, baseLocation)
        const isEdited: boolean = yield call(editModificationToDeleted, deletedMod, projectId, workspaceTool)

        handledModIds.add(savedMod.id)

        // Don't delete a wall's king studs if the wall itself couldn't be deleted.
        if (!isEdited) continue

        const { siblingModsToEdit, siblingModsToCreate } = collectWallSiblingDeletions(
            savedMod,
            baseLocation,
            drawableLocations,
            allSavedMods
        )

        for (const siblingMod of siblingModsToEdit) {
            if (handledModIds.has(siblingMod.id)) continue

            handledModIds.add(siblingMod.id)
            yield call(editModificationToDeleted, siblingMod, projectId, workspaceTool)
        }

        const modsToCreate = siblingModsToCreate.filter((mod) => !handledCreateKeys.has(getCreateKey(mod)))

        if (!modsToCreate.length) continue

        modsToCreate.forEach((mod) => handledCreateKeys.add(getCreateKey(mod)))

        try {
            const createdMods: IMaterialModificationConflictAPI[] = yield call(
                createModification,
                modsToCreate,
                projectId
            )

            yield all(
                createdMods.map((mod) =>
                    put(
                        updateSavedMaterialModification({
                            id: mod.id,
                            material_location_id: mod.material_location_id,
                            coordinates: mod.coordinates,
                            floor_plan_id: mod.floor_plan_id,
                            option_ids: mod.option_ids.split('_').map((opId) => parseInt(opId)),
                            cutouts: mod.cutouts,
                            shapeColor: mod.shapeColor,
                        })
                    )
                )
            )
        } catch (e) {
            yield call(console.error, e)

            continue
        }

        yield call(discardPendingModifications, modsToCreate, workspaceTool)
    }
}

// The deletion records now cover these locations and options, so their temporary review items must
// not stay on the canvas and pending edits must not be saved over the deletion.
function* discardPendingModifications(createdMods: IMaterialModificationConflict[], workspaceTool: Workspace) {
    const scopes = createdMods.map(({ material_location_id, option_ids }) => ({ material_location_id, option_ids }))

    yield put(discardMaterialModificationConflicts(scopes))

    const items: paper.Item[] | null = yield call(workspaceTool.getItemsWithCriteria, 'data', (data) => {
        if (!data?.isModification) return false

        const conflict: IMaterialModificationConflict | undefined = data.materialModificationConflict
        const locationId = conflict?.material_location_id ?? data.opening_location_id

        return scopes.some(
            (scope) =>
                scope.material_location_id === locationId &&
                (!conflict?.option_ids || scope.option_ids.some((id) => conflict.option_ids.includes(id)))
        )
    })

    if (items?.length) yield all(items.map((item) => call([item, 'remove'])))
}

const toDeletedModification = (
    mod: IMaterialModificationConflict,
    baseLocation: IMUP2DDrawableLocation
): IMaterialModificationConflict => ({
    ...cloneDeep(mod),
    coordinates: baseLocation.coordinates,
    cutouts: baseLocation.cutouts ?? [],
})

// The wall itself (as opposed to one of its king studs) is the multi-point WALL location.
const isWallLocation = (location: IMUP2DDrawableLocation) =>
    location.drawing_type === DRAWABLE_TYPES.WALL && location.coordinates.length > 1

// Deleting a wall deletes every location of that wall; deleting a king stud deletes only itself.
// Returns all of the wall's locations (including the wall) when `location` is the wall, else none.
const getWallLocations = (location: IMUP2DDrawableLocation, drawableLocations: IMUP2DDrawableLocation[]) =>
    isWallLocation(location) ? drawableLocations.filter((loc) => loc.drawable_id === location.drawable_id) : []

// Applies the wall cascade (see getWallLocations) to saved modifications, but only for the wall's
// own options -- a sibling's saved modification may cover additional options (e.g. a king stud
// shared, unchanged, across two option variants), and those must keep their existing geometry
// rather than being deleted along with the wall.
function collectWallSiblingDeletions(
    deletedMod: IMaterialModificationConflict,
    baseLocation: IMUP2DDrawableLocation,
    drawableLocations: IMUP2DDrawableLocation[],
    savedMods: IMaterialModificationConflict[]
) {
    const siblingModsToEdit: IMaterialModificationConflict[] = []
    const siblingModsToCreate: IMaterialModificationConflict[] = []

    getWallLocations(baseLocation, drawableLocations)
        .filter((loc) => loc.opening_location_id !== baseLocation.opening_location_id)
        .forEach((sibling) => {
            const existingMods = savedMods.filter((mod) => mod.material_location_id === sibling.opening_location_id)
            // Options the wall is being deleted for that still need a deletion record at this location.
            const uncoveredOptionIds = new Set(deletedMod.option_ids)
            // Options newly carved out of an existing record (by a split below) plus any options with
            // no existing record at all -- these are folded into a single new record at the end so the
            // deletion isn't scattered across several overlapping ones.
            const newlyDeletedOptionIds = new Set<number>()

            existingMods.forEach((mod) => {
                const overlappingOptionIds = mod.option_ids.filter((id) => uncoveredOptionIds.has(id))

                if (!overlappingOptionIds.length) return // none of this record's options are being deleted

                overlappingOptionIds.forEach((id) => uncoveredOptionIds.delete(id))

                const keptOptionIds = mod.option_ids.filter((id) => !overlappingOptionIds.includes(id))

                if (keptOptionIds.length) {
                    // Only some of this record's options are being deleted: narrow it to the options
                    // that keep their existing geometry, and let the deleted options fall through to a
                    // new record below -- rewriting it whole here would also delete the kept options.
                    siblingModsToEdit.push({ ...mod, option_ids: keptOptionIds })
                    overlappingOptionIds.forEach((id) => newlyDeletedOptionIds.add(id))
                } else {
                    // every option this record covers is being deleted -- rewrite it in place
                    siblingModsToEdit.push(toDeletedModification(mod, sibling))
                }
            })

            // Options with no existing modification at this location still need a deletion record.
            uncoveredOptionIds.forEach((id) => newlyDeletedOptionIds.add(id))

            if (newlyDeletedOptionIds.size) {
                siblingModsToCreate.push({
                    id: Number(sibling.opening_location_id + '0000'), // temporary ID, matches highlightOptionConflicts
                    material_location_id: sibling.opening_location_id,
                    coordinates: sibling.coordinates,
                    cutouts: sibling.cutouts ?? [],
                    floor_plan_id: sibling.document_chunk_id,
                    option_ids: [...newlyDeletedOptionIds],
                })
            }
        })

    return { siblingModsToEdit, siblingModsToCreate }
}

function* editModificationToDeleted(
    deletedMod: IMaterialModificationConflict,
    projectId: number,
    workspaceTool: Workspace
) {
    try {
        yield call(
            editModification,
            deletedMod.id,
            {
                material_location_id: deletedMod.material_location_id,
                coordinates: deletedMod.coordinates,
                floor_plan_id: deletedMod.floor_plan_id,
                option_ids: deletedMod.option_ids,
                cutouts: deletedMod.cutouts,
            },
            projectId
        )
    } catch (e) {
        yield call(console.error, e)

        return false
    }

    yield put(updateSavedMaterialModification(deletedMod))

    const items: paper.Item[] | null = yield call(
        workspaceTool.getItemsWithCriteria,
        'data',
        // Reviewed items aren't always tagged with materialModificationConflict. Fall back to the
        // location only for those -- a tagged item for another record at the same location (e.g. in
        // another active overlay) is a different modification and must stay.
        (data) => {
            if (!data?.isModification) return false

            const conflict = data.materialModificationConflict

            return conflict
                ? conflict.id === deletedMod.id
                : data.opening_location_id === deletedMod.material_location_id
        }
    )

    if (items?.length) yield all(items.map((item) => call([item, 'remove'])))

    return true
}

export function* handleDeleteModification(action: ReturnType<typeof deleteModificationAction>) {
    const manager: PaperManager | null = yield call(managers.get2DManager)

    if (!manager) return

    const [selectTool, workspaceTool]: [Select, Workspace] = yield call(manager.getTools, [Select.NAME, Workspace.NAME])

    const { materialModificationConflicts }: ReturnType<typeof selectMaterialModificationConflicts> = yield select(
        selectMaterialModificationConflicts
    )

    const drawableLocations: IMUP2DDrawableLocation[] = yield select(selectDrawableLocations)

    const materialModificationConflictsIdsSet = new Set(action.payload.map((id) => id))

    // Ids not among the pending conflicts belong to already-saved modifications being reviewed.
    const pendingConflictIds = new Set(materialModificationConflicts.map((m) => m.id))
    const savedMods: IMaterialModificationConflict[] = yield select(selectSavedMaterialModifications)
    const savedModsToDelete = savedMods.filter(
        (m) => materialModificationConflictsIdsSet.has(m.id) && !pendingConflictIds.has(m.id)
    )

    if (savedModsToDelete.length) {
        yield call(selectTool.exitSelectMode)
        yield call(markSavedModificationsAsDeleted, savedModsToDelete)
    }

    const selectedModificationLocationIds = materialModificationConflicts.reduce((ids, m) => {
        if (materialModificationConflictsIdsSet.has(m.id)) {
            return [...ids, m.material_location_id]
        }

        return ids
    }, [] as number[])

    const selectedWallLocations = drawableLocations.filter(
        (loc) => selectedModificationLocationIds.includes(loc.opening_location_id) && isWallLocation(loc)
    )

    const materialConflictsList = materialModificationConflicts.filter(
        (m) =>
            materialModificationConflictsIdsSet.has(m.id) ||
            // a selected wall (not a king stud) takes every location of that wall with it
            selectedWallLocations.some((wall) =>
                getWallLocations(wall, drawableLocations).some(
                    (loc) => loc.opening_location_id === m.material_location_id
                )
            )
    )

    // update coordinates to original state
    const materialConflictsIoDelete = materialConflictsList.map((m) => {
        const assignedLocation: IMUP2DDrawableLocation | undefined = drawableLocations.find(
            (loc) => loc.opening_location_id === m.material_location_id
        )
        const updatedMaterialConflict = cloneDeep(m)

        if (assignedLocation) {
            updatedMaterialConflict.coordinates = assignedLocation.coordinates
            updatedMaterialConflict.cutouts = assignedLocation.cutouts
        }

        return updatedMaterialConflict
    })

    yield put(updateMaterialModificationConflictToSave(materialConflictsIoDelete))

    // exit selected mode, remove handlers
    yield call(selectTool.exitSelectMode)

    const itemsToDelete: paper.Item[] | null = yield call(
        workspaceTool.getItemsWithCriteria,
        'data',
        (data) => !!materialConflictsIoDelete.find((mod) => data?.materialModificationConflict?.id === mod.id)
    )

    if (!itemsToDelete) return

    // remove items from blueprint
    yield all(itemsToDelete.map((item) => call([item, 'remove'])))
}

export function* watchForDeleteModification() {
    yield takeEvery(deleteModificationAction.type, handleDeleteModification)
}
